// Small JPEG thumbnails for task attachments.
//
// Lists and cards across the app render attachments, and every <video> mount
// (even with preload="metadata") fetches the moov atom. Multiplied across
// the team, that's what blew our cached egress past the free-tier cap. The
// fix is to generate a static thumbnail at upload time, store it next to the
// original in the same bucket, and render the thumb in every list/card. The
// original stays untouched at its existing URL — the team still downloads
// full-quality files for socials.
//
// Returns Blob | null. Null means "couldn't make a thumb, fall back to
// rendering the original (or a play-icon placeholder for videos)."

export const THUMB_MAX_PX = 480;
export const THUMB_QUALITY = 0.72;
const VIDEO_GEN_TIMEOUT_MS = 6000;

function scaleToFit(naturalW, naturalH, maxPx) {
  if (naturalW <= maxPx && naturalH <= maxPx) return { w: naturalW, h: naturalH };
  if (naturalW >= naturalH) {
    return { w: maxPx, h: Math.round((naturalH * maxPx) / naturalW) };
  }
  return { w: Math.round((naturalW * maxPx) / naturalH), h: maxPx };
}

function canvasToJpegBlob(canvas, q = THUMB_QUALITY) {
  return new Promise((resolve) => {
    canvas.toBlob((b) => resolve(b || null), "image/jpeg", q);
  });
}

export async function generateImageThumb(file) {
  if (!file || !file.type?.startsWith("image/")) return null;
  // SVG can't be drawn via Image() reliably across browsers without a
  // serialize-and-rasterize dance — and SVGs are tiny anyway. Skip.
  if (file.type === "image/svg+xml") return null;
  const objectUrl = URL.createObjectURL(file);
  try {
    return await new Promise((resolve) => {
      const img = new Image();
      img.onerror = () => resolve(null);
      img.onload = async () => {
        const { w, h } = scaleToFit(img.naturalWidth, img.naturalHeight, THUMB_MAX_PX);
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(await canvasToJpegBlob(c));
      };
      img.src = objectUrl;
    });
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export async function generateVideoThumb(file) {
  if (!file || !file.type?.startsWith("video/")) return null;
  const objectUrl = URL.createObjectURL(file);
  try {
    return await Promise.race([
      new Promise((resolve) => {
        const video = document.createElement("video");
        video.preload = "auto";
        video.muted = true;
        video.playsInline = true;
        // CORS isn't a factor here — we're loading from a blob: URL — but
        // setting this keeps the canvas un-tainted across all paths.
        video.crossOrigin = "anonymous";

        const cleanup = () => {
          video.removeAttribute("src");
          video.load();
        };

        video.onerror = () => {
          cleanup();
          resolve(null);
        };

        video.onloadedmetadata = () => {
          const target = Number.isFinite(video.duration)
            ? Math.min(1, Math.max(0.05, video.duration * 0.05))
            : 0.1;
          video.currentTime = target;
        };

        video.onseeked = async () => {
          try {
            const { w, h } = scaleToFit(
              video.videoWidth || THUMB_MAX_PX,
              video.videoHeight || THUMB_MAX_PX,
              THUMB_MAX_PX
            );
            const c = document.createElement("canvas");
            c.width = w;
            c.height = h;
            c.getContext("2d").drawImage(video, 0, 0, w, h);
            const blob = await canvasToJpegBlob(c);
            cleanup();
            resolve(blob);
          } catch {
            cleanup();
            resolve(null);
          }
        };

        video.src = objectUrl;
      }),
      new Promise((resolve) => setTimeout(() => resolve(null), VIDEO_GEN_TIMEOUT_MS)),
    ]);
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

// Used by the lazy-backfill path on old image attachments that lack a thumb.
// Pulls the original from its public Storage URL, generates the thumb client-
// side. This costs one fetch of the original — after that the thumb serves
// from CDN forever, so it pays for itself on the second render.
//
// Returns null if the bucket isn't sending CORS headers (canvas would taint),
// if the network fails, or if the image is unrecognized. Caller treats null
// as "leave the attachment as-is and fall back to original-render."
export async function generateImageThumbFromUrl(url) {
  if (!url || typeof url !== "string") return null;
  return await new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onerror = () => resolve(null);
    img.onload = async () => {
      try {
        const { w, h } = scaleToFit(img.naturalWidth, img.naturalHeight, THUMB_MAX_PX);
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        c.getContext("2d").drawImage(img, 0, 0, w, h);
        resolve(await canvasToJpegBlob(c));
      } catch {
        resolve(null);
      }
    };
    img.src = url;
  });
}
